import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BudgetTable = ({ data, onItemAction }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_track':
        return 'bg-success/10 text-success border-success/20';
      case 'over_budget':
        return 'bg-error/10 text-error border-error/20';
      case 'under_utilized':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'approaching_limit':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'on_track':
        return 'On Track';
      case 'over_budget':
        return 'Over Budget';
      case 'under_utilized':
        return 'Under Utilized';
      case 'approaching_limit':
        return 'Approaching Limit';
      default:
        return 'Unknown';
    }
  };

  const getUtilizationColor = (rate) => {
    if (rate >= 95) return 'text-error';
    if (rate >= 85) return 'text-warning';
    return 'text-success';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return data;

    return [...data]?.sort((a, b) => {
      if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const SortableHeader = ({ column, children }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/5"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        {sortConfig?.key === column && (
          <Icon 
            name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
            size={14} 
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon name="Table" size={20} className="text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Budget Allocation Details</h3>
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {sortedData?.length} budget items
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/5">
            <tr>
              <SortableHeader column="department">Department</SortableHeader>
              <SortableHeader column="category">Category</SortableHeader>
              <SortableHeader column="budgetCode">Budget Code</SortableHeader>
              <SortableHeader column="allocated">Allocated</SortableHeader>
              <SortableHeader column="spent">Spent</SortableHeader>
              <SortableHeader column="remaining">Remaining</SortableHeader>
              <SortableHeader column="utilizationRate">Utilization</SortableHeader>
              <SortableHeader column="status">Status</SortableHeader>
              <SortableHeader column="lastUpdate">Last Update</SortableHeader>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {sortedData?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/5 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Building2" size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{item?.department}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">{item?.category}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-mono text-muted-foreground">{item?.budgetCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-foreground">
                    {formatCurrency(item?.allocated)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {formatCurrency(item?.spent)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {formatCurrency(item?.remaining)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <div className={`text-sm font-medium ${getUtilizationColor(item?.utilizationRate)}`}>
                      {item?.utilizationRate}%
                    </div>
                    <div className="w-20 bg-muted rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          item?.utilizationRate >= 95 ? 'bg-error' : 
                          item?.utilizationRate >= 85 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${Math.min(item?.utilizationRate, 100)}%` }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(item?.status)}`}>
                    {getStatusText(item?.status)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  <div>
                    <div>{formatDate(item?.lastUpdate)}</div>
                    <div className="text-xs">by {item?.approver}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onItemAction('view_details', item)}
                      className="text-xs"
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onItemAction('edit', item)}
                      className="text-xs"
                    >
                      Edit
                    </Button>
                    {item?.status === 'pending_approval' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Check"
                        onClick={() => onItemAction('approve', item)}
                        className="text-xs text-success"
                      >
                        Approve
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table Footer */}
      <div className="px-6 py-4 border-t border-border bg-muted/5">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-2 lg:space-y-0">
          <div className="text-sm text-muted-foreground">
            Total Budget: {formatCurrency(sortedData?.reduce((sum, item) => sum + item?.allocated, 0))}
          </div>
          <div className="text-sm text-muted-foreground">
            Total Spent: {formatCurrency(sortedData?.reduce((sum, item) => sum + item?.spent, 0))}
          </div>
          <div className="text-sm text-foreground font-medium">
            Remaining: {formatCurrency(sortedData?.reduce((sum, item) => sum + item?.remaining, 0))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetTable;